import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { Activity, Search, Filter, Download, RefreshCw, Calendar, User, Database, Edit3 } from 'lucide-react';

const ActivityMonitor = ({ activities = [], onRefresh }) => {
  const [fullActivities, setFullActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    action: 'all',
    entity_type: 'all',
    date_range: 'all',
    user_id: 'all'
  });
  const [users, setUsers] = useState([]);

  // Activity action options
  const actionOptions = [
    { value: 'INSERT', label: 'Création', color: 'bg-green-100 text-green-800' },
    { value: 'UPDATE', label: 'Modification', color: 'bg-blue-100 text-blue-800' },
    { value: 'DELETE', label: 'Suppression', color: 'bg-red-100 text-red-800' }
  ];

  // Entity type options
  const entityOptions = [
    { value: 'properties', label: 'Propriétés', icon: '🏠' },
    { value: 'appointments', label: 'Rendez-vous', icon: '📅' },
    { value: 'users', label: 'Utilisateurs', icon: '👥' },
    { value: 'notifications', label: 'Notifications', icon: '🔔' },
    { value: 'frontend_content', label: 'Contenu', icon: '📝' }
  ];

  // Fetch detailed activity logs
  const fetchActivityLogs = async () => {
    try {
      setLoading(true);
      let query = supabase?.from('activity_logs')?.select(`
        *,
        user:user_profiles(full_name, email, role)
      `)?.order('created_at', { ascending: false })?.limit(500);

      // Apply filters
      if (filters?.action !== 'all') {
        query = query?.eq('action', filters?.action);
      }
      if (filters?.entity_type !== 'all') {
        query = query?.eq('entity_type', filters?.entity_type);
      }
      if (filters?.user_id !== 'all') {
        query = query?.eq('user_id', filters?.user_id);
      }

      // Date range filter
      if (filters?.date_range !== 'all') {
        const now = new Date();
        let startDate;
        
        switch (filters?.date_range) {
          case 'today':
            startDate = new Date(now?.getFullYear(), now?.getMonth(), now?.getDate());
            break;
          case 'week':
            startDate = new Date(now?.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case 'month':
            startDate = new Date(now?.getFullYear(), now?.getMonth(), 1);
            break;
        }
        
        if (startDate) {
          query = query?.gte('created_at', startDate?.toISOString());
        }
      }

      const { data, error } = await query;
      if (error) throw error;

      setFullActivities(data || []);
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      alert('Erreur lors du chargement des logs d\'activité');
    } finally {
      setLoading(false);
    }
  };

  // Fetch users for filtering
  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('id, full_name, role')?.order('full_name');
      if (error) throw error;

      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    fetchActivityLogs();
    fetchUsers();
  }, [filters]);

  // Filter activities based on search term
  const filteredActivities = fullActivities?.filter(activity => 
    activity?.action?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    activity?.entity_type?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    activity?.user?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    activity?.ip_address?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  // Get action details
  const getActionDetails = (action) => {
    return actionOptions?.find(opt => opt?.value === action) || { 
      value: action, 
      label: action, 
      color: 'bg-gray-100 text-gray-800' 
    };
  };

  // Get entity details
  const getEntityDetails = (entityType) => {
    return entityOptions?.find(opt => opt?.value === entityType) || { 
      value: entityType, 
      label: entityType, 
      icon: '📊' 
    };
  };

  // Format date and time
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date?.toLocaleDateString('fr-FR'),
      time: date?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    };
  };

  // Parse activity details
  const parseActivityDetails = (activity) => {
    try {
      if (activity?.details && typeof activity?.details === 'object') {
        return activity?.details;
      }
      return JSON.parse(activity?.details || '{}');
    } catch {
      return {};
    }
  };

  // Export activity logs
  const exportActivityLogs = () => {
    const csvContent = "data:text/csv;charset=utf-8," + "Date,Heure,Action,Entité,Utilisateur,Adresse IP,User Agent,Détails\n" +
      filteredActivities?.map(activity => {
        const dateTime = formatDateTime(activity?.created_at);
        const actionDetails = getActionDetails(activity?.action);
        const entityDetails = getEntityDetails(activity?.entity_type);
        const details = parseActivityDetails(activity);
        
        return [
          dateTime?.date,
          dateTime?.time,
          actionDetails?.label,
          entityDetails?.label,
          activity?.user?.full_name || 'Système',
          activity?.ip_address || 'N/A',
          activity?.user_agent || 'N/A',
          JSON.stringify(details)
        ]?.join(',');
      })?.join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link?.setAttribute("href", encodedUri);
    link?.setAttribute("download", `activity_logs_${new Date()?.toISOString()?.split('T')?.[0]}.csv`);
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  };

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Activity className="w-5 h-5 mr-2 text-purple-600" />
              Moniteur d'Activité Système
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {filteredActivities?.length} activité(s) trouvée(s)
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Rechercher activités..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            {/* Export Button */}
            <button
              onClick={exportActivityLogs}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Exporter CSV</span>
            </button>
            
            {/* Refresh Button */}
            <button
              onClick={() => {
                onRefresh?.();
                fetchActivityLogs();
              }}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Actualiser</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
            <select
              value={filters?.action}
              onChange={(e) => setFilters({...filters, action: e?.target?.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Toutes les actions</option>
              {actionOptions?.map(option => (
                <option key={option?.value} value={option?.value}>{option?.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Entité</label>
            <select
              value={filters?.entity_type}
              onChange={(e) => setFilters({...filters, entity_type: e?.target?.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Toutes les entités</option>
              {entityOptions?.map(option => (
                <option key={option?.value} value={option?.value}>{option?.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Utilisateur</label>
            <select
              value={filters?.user_id}
              onChange={(e) => setFilters({...filters, user_id: e?.target?.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Tous les utilisateurs</option>
              {users?.map(user => (
                <option key={user?.id} value={user?.id}>
                  {user?.full_name} ({user?.role})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Période</label>
            <select
              value={filters?.date_range}
              onChange={(e) => setFilters({...filters, date_range: e?.target?.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Toutes les dates</option>
              <option value="today">Aujourd'hui</option>
              <option value="week">Cette semaine</option>
              <option value="month">Ce mois</option>
            </select>
          </div>
        </div>
      </div>

      {/* Activity Timeline */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {loading && (
          <div className="p-8 text-center">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Chargement des activités...</p>
          </div>
        )}

        {!loading && filteredActivities?.length > 0 && (
          <div className="max-h-96 overflow-y-auto">
            {filteredActivities?.map((activity, index) => {
              const dateTime = formatDateTime(activity?.created_at);
              const actionDetails = getActionDetails(activity?.action);
              const entityDetails = getEntityDetails(activity?.entity_type);
              const details = parseActivityDetails(activity);

              return (
                <div key={activity?.id} className={`p-4 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 ${index < 10 ? 'bg-blue-50' : ''}`}>
                  <div className="flex items-start space-x-4">
                    {/* Timeline Dot */}
                    <div className={`w-3 h-3 rounded-full mt-2 flex-shrink-0 ${
                      activity?.action === 'INSERT' ? 'bg-green-500' :
                      activity?.action === 'UPDATE' ? 'bg-blue-500' :
                      activity?.action === 'DELETE'? 'bg-red-500' : 'bg-gray-500'
                    }`}></div>

                    {/* Activity Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${actionDetails?.color}`}>
                            {actionDetails?.label}
                          </span>
                          <span className="text-sm text-gray-600">
                            {entityDetails?.icon} {entityDetails?.label}
                          </span>
                        </div>
                        
                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                          <div className="flex items-center">
                            <Calendar className="w-3 h-3 mr-1" />
                            <span>{dateTime?.date} {dateTime?.time}</span>
                          </div>
                        </div>
                      </div>

                      <div className="mt-1 text-sm text-gray-900">
                        {activity?.user?.full_name ? (
                          <div className="flex items-center space-x-2">
                            <User className="w-4 h-4 text-gray-400" />
                            <span>
                              <strong>{activity?.user?.full_name}</strong> ({activity?.user?.role})
                              {activity?.action === 'INSERT' && ' a créé '}
                              {activity?.action === 'UPDATE' && ' a modifié '}
                              {activity?.action === 'DELETE' && ' a supprimé '}
                              {entityDetails?.label?.toLowerCase()}
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <Database className="w-4 h-4 text-gray-400" />
                            <span>Système - {actionDetails?.label} {entityDetails?.label?.toLowerCase()}</span>
                          </div>
                        )}
                      </div>

                      {/* Additional Details */}
                      {activity?.ip_address && (
                        <div className="mt-2 text-xs text-gray-500">
                          <strong>IP:</strong> {activity?.ip_address}
                          {activity?.user_agent && (
                            <span className="ml-4">
                              <strong>Navigateur:</strong> {activity?.user_agent?.substring(0, 50)}...
                            </span>
                          )}
                        </div>
                      )}

                      {/* Entity ID */}
                      {activity?.entity_id && (
                        <div className="mt-1 text-xs text-gray-500">
                          <strong>ID Entité:</strong> {activity?.entity_id}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && filteredActivities?.length === 0 && (
          <div className="text-center py-12">
            <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Aucune activité trouvée pour les critères sélectionnés</p>
          </div>
        )}
      </div>

      {/* Activity Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">Créations</p>
              <p className="text-2xl font-bold text-green-900">
                {filteredActivities?.filter(a => a?.action === 'INSERT')?.length}
              </p>
            </div>
            <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
              <span className="text-white text-sm font-bold">+</span>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600">Modifications</p>
              <p className="text-2xl font-bold text-blue-900">
                {filteredActivities?.filter(a => a?.action === 'UPDATE')?.length}
              </p>
            </div>
            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
              <Edit3 className="w-4 h-4 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-red-600">Suppressions</p>
              <p className="text-2xl font-bold text-red-900">
                {filteredActivities?.filter(a => a?.action === 'DELETE')?.length}
              </p>
            </div>
            <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center">
              <span className="text-white text-sm font-bold">-</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityMonitor;